import apiV2Service from 'common/src/modules/api/api-v2.service';
import enumsService from 'common/src/modules/enums/enums.service';
import {CLEnumType} from 'common/src/modules/enums/enums.types';
import {
  toClientAdservingPartnerInfo,
  toClientCreativeSpec,
  toClientDataSharing,
  toClientMacros,
  toLocalAdservingPartnerWithMacros,
  toLocalPartnerData,
  toRemoteAdservingPartnerInfo,
  toRemoteAdServingPartnerWithMacros,
  toRemoteCreativeSpec,
  toRemoteDataSharing,
  toRemoteMacros,
} from './adServingPartner.dataConverter';
import {
  AdServingPartnerData,
  AdservingPartnerInfo_UI,
  AdservingPartnerWithMacros,
  CreativeSpec_UI,
  DataSharing_UI,
  Macros_UI,
  RemoteAdServingPartnerData,
  RemoteAdservingPartnerInfo_UI,
  RemoteAdservingPartnerWithMacros,
  RemoteCreativeSpec_UI,
  RemoteDataSharing_UI,
  RemoteMacros_UI,
  RemoteSearchDspsResponse,
  SearchDspsResponse,
} from './adServingPartner.types';
import {DateTime} from 'luxon';
import getTimeZoneStringForDateTime from '../timezones/utils/getTimezoneStringForDateTime';
import {UTCTimezoneObject} from '../timezones/timezones.consts';

const adServingPartnersPath = 'AdservingPartner';

async function getPartnersList(): Promise<AdServingPartnerData[]> {
  const {data} =
    await apiV2Service.executeGetRequest<RemoteAdServingPartnerData[]>(adServingPartnersPath);

  return data.map((partner) => toLocalPartnerData(partner, data));
}

async function getAccountPartnersList(clientId: number): Promise<AdServingPartnerData[]> {
  const {data} = await apiV2Service.executeGetRequest<RemoteAdServingPartnerData[]>(
    `${adServingPartnersPath}/account`,
    {
      clientId,
    },
  );

  return data.map((partner) => toLocalPartnerData(partner, data));
}

async function savePartner(partner: AdservingPartnerWithMacros): Promise<void> {
  const campaignEnums = await enumsService.loadEnums(CLEnumType.CampaignPage);
  const dspWithMacrosUi = toRemoteAdServingPartnerWithMacros(partner, campaignEnums);
  await apiV2Service.executePutRequest(
    partner.partner.id ? `${adServingPartnersPath}/${partner.partner.id}` : adServingPartnersPath,
    {},
    {...dspWithMacrosUi},
  );
}

async function getPartnerById(id: number): Promise<AdservingPartnerWithMacros> {
  const {data} = await apiV2Service.executeGetRequest<RemoteAdservingPartnerWithMacros>(
    `${adServingPartnersPath}/${id}`,
  );

  return toLocalAdservingPartnerWithMacros(data);
}

async function searchDspsWithEmbeddings(prompt: string): Promise<SearchDspsResponse> {
  const {data} = await apiV2Service.executeGetRequest<RemoteSearchDspsResponse>(
    `${adServingPartnersPath}/SearchDspsWithEmbeddings`,
    {
      prompt,
    },
  );

  const {dsps, filtersExplanation} = data;

  return {
    dsps: dsps.map(toLocalAdservingPartnerWithMacros),
    filtersExplanation,
  };
}

async function getPartnerInfo(partnerId: number): Promise<AdservingPartnerInfo_UI> {
  const {data} = await apiV2Service.executeGetRequest<RemoteAdservingPartnerInfo_UI>(
    `${adServingPartnersPath}/${partnerId}/info`,
  );

  return toClientAdservingPartnerInfo(data);
}

async function updatePartnerInfo(
  partnerId: number,
  partnerInfo: AdservingPartnerInfo_UI,
): Promise<void> {
  const remotePartnerInfo = toRemoteAdservingPartnerInfo(partnerInfo);

  await apiV2Service.executePutRequest<void>(
    `${adServingPartnersPath}/${partnerId}/info`,
    {},
    remotePartnerInfo as unknown as Record<string, unknown>,
  );
}

async function getCreativeSpec(partnerId: number): Promise<CreativeSpec_UI> {
  const {data} = await apiV2Service.executeGetRequest<RemoteCreativeSpec_UI>(
    `${adServingPartnersPath}/${partnerId}/creative-spec`,
  );

  return toClientCreativeSpec(data);
}

async function updateCreativeSpec(partnerId: number, creativeSpec: CreativeSpec_UI): Promise<void> {
  const remoteCreativeSpec = toRemoteCreativeSpec(creativeSpec);

  await apiV2Service.executePutRequest<void>(
    `${adServingPartnersPath}/${partnerId}/creative-spec`,
    {},
    remoteCreativeSpec as unknown as Record<string, unknown>,
  );
}

async function getMacros(partnerId: number): Promise<Macros_UI> {
  const {data} = await apiV2Service.executeGetRequest<RemoteMacros_UI>(
    `${adServingPartnersPath}/${partnerId}/macros`,
  );

  return toClientMacros(data);
}

async function updateMacros(partnerId: number, macros: Macros_UI): Promise<void> {
  const remoteMacros = toRemoteMacros(macros);

  await apiV2Service.executePutRequest<void>(
    `${adServingPartnersPath}/${partnerId}/macros`,
    {},
    {
      macros: remoteMacros.macros || [],
    },
  );
}

async function getDataSharing(partnerId: number): Promise<DataSharing_UI> {
  const {data} = await apiV2Service.executeGetRequest<RemoteDataSharing_UI>(
    `${adServingPartnersPath}/${partnerId}/data-sharing`,
  );

  return toClientDataSharing(data);
}

async function updateDataSharing(partnerId: number, dataSharing: DataSharing_UI): Promise<void> {
  const remoteDataSharing = toRemoteDataSharing(dataSharing);

  await apiV2Service.executePutRequest<void>(
    `${adServingPartnersPath}/${partnerId}/data-sharing`,
    {},
    remoteDataSharing as unknown as Record<string, unknown>,
  );
}

async function createFromInfo(
  clientId: string,
  partnerInfo: AdservingPartnerInfo_UI,
): Promise<number> {
  const remotePartnerInfo = toRemoteAdservingPartnerInfo(partnerInfo);

  const {data} = await apiV2Service.executePutRequest<number>(
    `${adServingPartnersPath}/info`,
    {clientId},
    remotePartnerInfo as unknown as Record<string, unknown>,
  );

  return data;
}

async function getAdServingDspHealthCheckerJobExecutionId({
  campaignId,
  date,
  adServingPartnerId,
}: {
  campaignId: number;
  adServingPartnerId: number;
  date: DateTime;
}): Promise<number> {
  const timezone = getTimeZoneStringForDateTime(UTCTimezoneObject);
  const {data} = await apiV2Service.executePostRequest<string>(
    `${adServingPartnersPath}/RunDspHealthReportJob`,
    {},
    {
      dspId: adServingPartnerId,
      date: date.setZone(timezone, {keepLocalTime: true}).toISO(),
      ...(campaignId ? {campaignId} : {}),
    },
  );

  return +data;
}

async function getClientsByDspId(dspId: number): Promise<number[]> {
  const {data} = await apiV2Service.executeGetRequest<number[]>(
    `${adServingPartnersPath}/GetClientsByDspId`,
    {dspId},
  );

  return data;
}

async function getCampaignsByDspId(
  clientId: number,
  dspId: number,
): Promise<Record<string, string>> {
  const {data} = await apiV2Service.executeGetRequest<Record<string, string>>(
    `${clientId}/${adServingPartnersPath}/GetCampaignsByDspId`,
    {dspId},
    {includeClientId: false},
  );

  return data;
}

export const adServingPartnersApi = {
  getPartnersList,
  getPartnerById,
  savePartner,
  getAccountPartnersList,
  searchDspsWithEmbeddings,
  getPartnerInfo,
  updatePartnerInfo,
  createFromInfo,
  getCreativeSpec,
  updateCreativeSpec,
  getMacros,
  updateMacros,
  getDataSharing,
  updateDataSharing,
  getAdServingDspHealthCheckerJobExecutionId,
  getClientsByDspId,
  getCampaignsByDspId,
};
